/**
 * Zeitraum und Datum in der Kopfleiste — aus `ui_kits/dashboard/TopBar.jsx`
 * (HM-076). Tag, Woche, Monat, Jahr; davor und danach; ein Kalender mit
 * Schnellauswahl.
 *
 * **Die Kopfleiste rechnet keine Zeitzone.** Was ein Tag der Anlage ist, wo
 * heute für sie ist und ob es ein „danach" gibt, sagt der Server in `period`
 * (HM-076); diese Datei schiebt nur Kalenderdaten — „drei Tage später" ist
 * eine Frage an den Kalender und keine an die Uhr, und sie wird auf reinen
 * `YYYY-MM-DD`-Daten in UTC gerechnet, wo es keine Umstellung gibt. Bis die
 * Antwort da ist, steht der Anker so, wie die Adresse ihn nennt.
 *
 * **Der Zeitraum steht in der Adresse**, nicht in einem `useState` (HM-037).
 * Jeder Klick hier ist eine Navigation, und „der März in diesem Haus" ist ein
 * Link.
 *
 * **Die Adresse trägt den gewählten Tag, nicht den Anker des Zeitraums.** Der
 * Server nimmt jedes Datum innerhalb eines Zeitraums an und nennt den Anker
 * selbst (`PeriodInfo.date`). Bis dahin rundete jeder Klick auf den Anker —
 * Montag, Monatserster, 1. Januar — und ein Wechsel des Zeitraums ging vom
 * Anker des vorigen aus: Tag → Jahr → Tag landete auf dem 1. Januar, „heute"
 * → Woche → Tag auf dem Montag. Der Tag, für den sich jemand interessiert,
 * bleibt seither über jeden Wechsel stehen; Woche, Monat und Jahr sind die
 * Zeiträume **um diesen Tag**. Erst „davor" und „danach" wechseln den Tag —
 * auf den Anker des Nachbarzeitraums, denn welcher Tag dort gemeint ist, weiß
 * niemand.
 */
import { useEffect, useRef, useState } from "react";
import { IconButton, SegmentedControl, Select, icons, useNarrow } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import type { EnergyPeriod } from "../lib/router.js";

type PeriodInfo = ApiComponents["schemas"]["PeriodInfo"];

export const PERIOD_LABELS: Record<EnergyPeriod, string> = {
  day: "Tag",
  week: "Woche",
  month: "Monat",
  year: "Jahr",
};

/** Was die Kacheln unter ihrem Wert sagen — „über den Tag", „diese Woche". */
export const PERIOD_CAPTIONS: Record<EnergyPeriod, string> = {
  day: "über den Tag",
  week: "in der Woche",
  month: "im Monat",
  year: "im Jahr",
};

const MONTHS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

/* ---- Kalenderarithmetik auf Datumsstrings, ohne Zeitzone ----------------- */

function parse(date: string): { y: number; m: number; d: number } {
  const [y = 0, m = 1, d = 1] = date.split("-").map(Number);
  return { y, m, d };
}

function iso(y: number, m: number, d: number): string {
  return new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);
}

export function shiftDate(date: string, by: { days?: number; months?: number; years?: number }): string {
  const { y, m, d } = parse(date);
  return iso(y + (by.years ?? 0), m + (by.months ?? 0), d + (by.days ?? 0));
}

/** Montag der Woche, in der das Datum liegt. */
function mondayOf(date: string): string {
  const weekday = (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7;
  return shiftDate(date, { days: -weekday });
}

/**
 * Was in der Adresse steht: der Tag — oder nichts, wenn er heute ist. Der
 * Link auf „heute" soll morgen noch „heute" zeigen, nicht den gestrigen Tag.
 * Verglichen wird der **Tag**, nicht der Anker: Der Montag dieser Woche ist
 * nicht heute, und wer ihn gewählt hat, soll ihn beim Wechsel auf „Tag"
 * wiederfinden. Solange der Server noch nicht gesagt hat, was heute ist,
 * bleibt der Tag stehen.
 */
export function addressDate(date: string | null, today: string | null): string | null {
  return date === null || date === today ? null : date;
}

/** Der Anker eines Zeitraums um ein Datum — sein erster Tag. */
export function anchorOf(period: EnergyPeriod, date: string): string {
  const { y, m } = parse(date);
  switch (period) {
    case "day":
      return date;
    case "week":
      return mondayOf(date);
    case "month":
      return iso(y, m, 1);
    case "year":
      return iso(y, 1, 1);
  }
}

/* ---- Beschriftung -------------------------------------------------------- */

/**
 * Die Beschriftung des Zeitraums, aus der Antwort des Servers und in der
 * Zeitzone der Anlage. Der letzte Tag eines Zeitraums ist `to` minus zwölf
 * Stunden — `to` ist die Mitternacht danach, und zwölf Stunden davor liegt
 * sicher noch im letzten Tag, auch an einem Umstellungstag.
 */
export function periodLabel(
  info: PeriodInfo,
  timeZone: string,
  compact = false,
  /** Sehr schmal: das laufende Jahr weglassen — „Mi., 7. Okt." statt „Mi., 7. Okt. 2…". */
  tiny = false,
): string {
  const from = new Date(info.from);
  const last = new Date(new Date(info.to).getTime() - 12 * 3_600_000);
  const fmt = (options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat("de-DE", { timeZone, ...options });
  /* Schmal: der Monat kurz — „Mi, 2. Sept. 2026" statt „Mittwoch, 2. September 2026",
     damit die Zeile neben dem Zeitraum-Wähler auf ein Telefon passt. */
  const month = compact ? "short" : "long";
  /* Ganz schmal (320 px) passt auch das nicht; das Jahr fällt dann weg, aber nur
     das laufende — ein Tag im Vorjahr ohne Jahreszahl wäre eine falsche Auskunft. */
  const yearOf = (instant: Date) => fmt({ year: "numeric" }).format(instant);
  const currentYear = info.today.slice(0, 4);
  const year: "numeric" | undefined =
    tiny && yearOf(from) === currentYear && yearOf(last) === currentYear ? undefined : "numeric";
  switch (info.kind) {
    case "day":
      return fmt({ weekday: "short", day: "numeric", month, ...(year ? { year } : {}) }).format(from);
    case "week": {
      const sameMonth = fmt({ month: "numeric" }).format(from) === fmt({ month: "numeric" }).format(last);
      const start = sameMonth
        ? fmt({ day: "numeric" }).format(from)
        : fmt({ day: "numeric", month: "short" }).format(from);
      return `KW${info.isoWeek ?? "?"} · ${start} – ${fmt({ day: "numeric", month, ...(year ? { year } : {}) }).format(last)}`;
    }
    case "month":
      return fmt({ month, year: "numeric" }).format(from);
    case "year":
      return fmt({ year: "numeric" }).format(from);
  }
}

/* ---- Die Steuerung ------------------------------------------------------- */

export function PeriodControls({
  period,
  date,
  info,
  timeZone,
  onChange,
}: {
  period: EnergyPeriod;
  /** Der Anker aus der Adresse; `null` heißt der aktuelle Zeitraum. */
  date: string | null;
  /** Der aufgelöste Zeitraum vom Server — solange er fehlt, gibt es kein „danach". */
  info: PeriodInfo | null;
  timeZone: string;
  onChange: (period: EnergyPeriod, date: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const narrow = useNarrow(860);
  const tiny = useNarrow(380);
  const today = info?.today ?? null;
  /* Der Tag, um den es geht — aus der Adresse, sonst heute. Nicht `info.date`:
     Das ist der Anker des Zeitraums, und ein Wechsel von dort aus verliert den
     Tag (siehe Kopf der Datei). */
  const anchor = date ?? today;

  const go = (nextPeriod: EnergyPeriod, nextDate: string | null): void => {
    onChange(nextPeriod, addressDate(nextDate, today));
  };

  const periodOptions = (Object.keys(PERIOD_LABELS) as EnergyPeriod[]).map((value) => ({
    value,
    label: PERIOD_LABELS[value],
  }));

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: narrow ? 8 : 12,
        /* Breit dürfen beide umbrechen; schmal stehen sie in **einer** Zeile —
           der Zeitraum als knappes Auswahlfeld, die Datumsnavigation nimmt den
           Rest. Zwei Zeilen kosteten auf einem Telefon den halben ersten
           Bildschirm für zwei Einstellungen (HM-078). */
        flexWrap: narrow ? "nowrap" : "wrap",
        width: narrow ? "100%" : "auto",
      }}
    >
      {narrow ? (
        <Select
          label="Zeitraum"
          labelHidden
          size="sm"
          value={period}
          onChange={(event) => go(event.target.value as EnergyPeriod, date)}
          options={periodOptions}
          style={{ flexShrink: 0 }}
        />
      ) : (
        <SegmentedControl
          label="Zeitraum"
          size="sm"
          value={period}
          onChange={(next) => go(next, date)}
          options={periodOptions}
        />
      )}
      <div
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
          padding: 2,
          flex: narrow ? "1 1 0" : "0 0 auto",
          minWidth: 0,
          maxWidth: "100%",
          borderRadius: "var(--radius-md)",
          background: "var(--surface-inset)",
          border: "1px solid var(--border-subtle)",
        }}
      >
        <IconButton
          label="Vorheriger Zeitraum"
          variant="ghost"
          size="sm"
          /* Kein Vorgänger: Vor dem Einzug eines Bewohners endet der Weg zurück (HM-190). */
          disabled={info === null || info.previous === null}
          onClick={() => info?.previous != null && go(period, info.previous)}
        >
          <span style={{ display: "inline-flex", transform: "rotate(180deg)" }}>
            <icons.IconChevronRight size={17} />
          </span>
        </IconButton>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          title="Zeitraum wählen"
          aria-haspopup="dialog"
          aria-expanded={open}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 7,
            height: 30,
            padding: "0 8px",
            border: "none",
            background: open ? "var(--surface-card)" : "transparent",
            cursor: "pointer",
            borderRadius: "var(--radius-sm)",
            fontFamily: "var(--font-body)",
            fontSize: 13,
            fontWeight: 600,
            color: "var(--text-strong)",
            flex: "1 1 auto",
            minWidth: 0,
          }}
        >
          <span style={{ display: "inline-flex", flexShrink: 0 }}>
            <icons.IconCalendar size={15} />
          </span>
          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {info === null ? (date ?? "…") : periodLabel(info, timeZone, narrow, tiny)}
          </span>
        </button>
        <IconButton
          label="Nächster Zeitraum"
          variant="ghost"
          size="sm"
          disabled={info === null || info.next === null}
          onClick={() => info?.next != null && go(period, info.next)}
        >
          <icons.IconChevronRight size={17} />
        </IconButton>
        {open && anchor !== null && today !== null && (
          <PeriodPicker
            period={period}
            anchor={anchor}
            today={today}
            onPick={(nextPeriod, nextDate) => {
              go(nextPeriod, nextDate);
              setOpen(false);
            }}
            onClose={() => setOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Schnellauswahl und Kalender. **Die Schnellauswahl steht schmal über dem
 * Kalender** statt daneben — 420 Pixel Breite passen auf kein Telefon.
 */
function PeriodPicker({
  period,
  anchor,
  today,
  onPick,
  onClose,
}: {
  period: EnergyPeriod;
  anchor: string;
  today: string;
  onPick: (period: EnergyPeriod, date: string) => void;
  onClose: () => void;
}) {
  const narrow = useNarrow(560);
  const ref = useRef<HTMLDivElement>(null);
  const [view, setView] = useState(() => anchorOf("month", anchor));

  useEffect(() => {
    const onDown = (event: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(event.target as Node)) onClose();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const quick: readonly { label: string; period: EnergyPeriod; date: string }[] = [
    { label: "Heute", period: "day", date: today },
    { label: "Gestern", period: "day", date: shiftDate(today, { days: -1 }) },
    { label: "Diese Woche", period: "week", date: today },
    { label: "Letzte Woche", period: "week", date: shiftDate(today, { days: -7 }) },
    { label: "Dieser Monat", period: "month", date: today },
    { label: "Letzter Monat", period: "month", date: shiftDate(anchorOf("month", today), { months: -1 }) },
    { label: "Dieses Jahr", period: "year", date: today },
  ];
  const isActive = (entry: (typeof quick)[number]): boolean =>
    entry.period === period && anchorOf(entry.period, entry.date) === anchorOf(period, anchor);

  const { y: viewYear, m: viewMonth } = parse(view);
  const decadeStart = Math.floor(viewYear / 12) * 12;
  const cell = (
    on: boolean,
    isNow: boolean,
    extra: React.CSSProperties = {},
  ): React.CSSProperties => ({
    border: "none",
    borderRadius: "var(--radius-sm)",
    cursor: "pointer",
    fontFamily: "var(--font-body)",
    fontSize: 13,
    fontWeight: on || isNow ? 700 : 500,
    background: on ? "var(--hm-green-500)" : "transparent",
    color: on ? "var(--text-invert)" : isNow ? "var(--hm-green-700)" : "var(--text-body)",
    outline: !on && isNow ? "1px solid var(--hm-green-300)" : "none",
    ...extra,
  });
  const head = (label: string, onPrev: () => void, onNext: () => void, prevLabel: string, nextLabel: string) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
      <IconButton label={prevLabel} variant="ghost" size="sm" onClick={onPrev}>
        <span style={{ display: "inline-flex", transform: "rotate(180deg)" }}>
          <icons.IconChevronRight size={16} />
        </span>
      </IconButton>
      <span style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700, color: "var(--text-strong)" }}>
        {label}
      </span>
      <IconButton label={nextLabel} variant="ghost" size="sm" onClick={onNext}>
        <icons.IconChevronRight size={16} />
      </IconButton>
    </div>
  );

  let detail: React.ReactNode;
  if (period === "month") {
    const { y: anchorYear, m: anchorMonth } = parse(anchor);
    const { y: todayYear, m: todayMonth } = parse(today);
    detail = (
      <div style={{ padding: 12, width: 250 }}>
        {head(String(viewYear), () => setView(iso(viewYear - 1, 1, 1)), () => setView(iso(viewYear + 1, 1, 1)), "Vorheriges Jahr", "Nächstes Jahr")}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
          {MONTHS.map((name, index) => {
            const month = index + 1;
            const on = viewYear === anchorYear && month === anchorMonth;
            const isNow = viewYear === todayYear && month === todayMonth;
            const future = iso(viewYear, month, 1) > today;
            return (
              <button key={name} type="button" disabled={future} onClick={() => onPick("month", iso(viewYear, month, 1))} style={cell(on, isNow, { height: 40, opacity: future ? 0.4 : 1 })}>
                {name}
              </button>
            );
          })}
        </div>
      </div>
    );
  } else if (period === "year") {
    const { y: anchorYear } = parse(anchor);
    const { y: todayYear } = parse(today);
    detail = (
      <div style={{ padding: 12, width: 250 }}>
        {head(`${decadeStart} – ${decadeStart + 11}`, () => setView(iso(viewYear - 12, 1, 1)), () => setView(iso(viewYear + 12, 1, 1)), "Zurück", "Weiter")}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
          {Array.from({ length: 12 }, (_, k) => decadeStart + k).map((year) => (
            <button key={year} type="button" disabled={year > todayYear} onClick={() => onPick("year", iso(year, 1, 1))} style={cell(year === anchorYear, year === todayYear, { height: 40, fontFamily: "var(--font-mono)", opacity: year > todayYear ? 0.4 : 1 })}>
              {year}
            </button>
          ))}
        </div>
      </div>
    );
  } else {
    const isWeek = period === "week";
    const anchorMonday = mondayOf(anchor);
    const firstWeekday = (new Date(`${iso(viewYear, viewMonth, 1)}T00:00:00Z`).getUTCDay() + 6) % 7;
    const daysInMonth = new Date(Date.UTC(viewYear, viewMonth, 0)).getUTCDate();
    const cells: (string | null)[] = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) cells.push(iso(viewYear, viewMonth, day));
    const monthLabel = new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric", timeZone: "UTC" }).format(
      new Date(`${view}T00:00:00Z`),
    );
    detail = (
      <div style={{ padding: 12, width: 250 }}>
        {head(monthLabel, () => setView(shiftDate(view, { months: -1 })), () => setView(shiftDate(view, { months: 1 })), "Vorheriger Monat", "Nächster Monat")}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
          {WEEKDAYS.map((name) => (
            <div key={name} style={{ textAlign: "center", fontSize: 11, fontWeight: 600, color: "var(--text-faint)", padding: "2px 0 4px" }}>
              {name}
            </div>
          ))}
          {cells.map((day, index) => {
            if (day === null) return <div key={`leer-${index}`} />;
            const on = isWeek ? mondayOf(day) === anchorMonday : day === anchor;
            const isToday = day === today;
            const future = day > today;
            return (
              <button
                key={day}
                type="button"
                disabled={future}
                onClick={() => onPick(isWeek ? "week" : "day", day)}
                style={{
                  ...cell(on, isToday, { height: 30, fontFamily: "var(--font-mono)", fontSize: 12.5, opacity: future ? 0.4 : 1 }),
                  ...(isWeek && on ? { background: "var(--hm-green-100)", color: "var(--hm-green-800)", borderRadius: 0 } : {}),
                }}
              >
                {parse(day).d}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Zeitraum wählen"
      style={{
        position: "absolute",
        top: "calc(100% + 8px)",
        right: 0,
        zIndex: 200,
        display: "flex",
        flexDirection: narrow ? "column" : "row",
        maxWidth: "calc(100vw - 28px)",
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-lg)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: narrow ? "grid" : "flex",
          gridTemplateColumns: narrow ? "repeat(auto-fit, minmax(120px, 1fr))" : undefined,
          flexDirection: "column",
          gap: 2,
          padding: 10,
          borderRight: narrow ? "none" : "1px solid var(--border-subtle)",
          borderBottom: narrow ? "1px solid var(--border-subtle)" : "none",
          background: "var(--surface-inset)",
          minWidth: narrow ? 0 : 156,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--text-faint)", padding: "4px 10px 8px", gridColumn: "1 / -1" }}>
          Schnellauswahl
        </div>
        {quick.map((entry) => {
          const on = isActive(entry);
          return (
            <button
              key={entry.label}
              type="button"
              onClick={() => onPick(entry.period, entry.date)}
              style={{
                textAlign: "left",
                padding: "8px 10px",
                border: "none",
                borderRadius: "var(--radius-sm)",
                cursor: "pointer",
                fontFamily: "var(--font-body)",
                fontSize: 13,
                fontWeight: on ? 700 : 500,
                background: on ? "var(--hm-green-50)" : "transparent",
                color: on ? "var(--hm-green-700)" : "var(--text-body)",
              }}
            >
              {entry.label}
            </button>
          );
        })}
      </div>
      {detail}
    </div>
  );
}
